import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { CalendarDays, Heart, Play } from "lucide-react";

import Button from "./ui/Button";
import Chip from "./ui/Chip";
import TasteTag from "./ui/TasteTag";
import PosterPlaceholder from "./ui/PosterPlaceholder";
import RatingBadge from "./ui/RatingBadge";
import SeasonBadge from "./ui/SeasonBadge";
import Typography from "./ui/Typography";
import { cn } from "../utils/classNames";
import { formatReleaseDate } from "../utils/formatReleaseDate";
import { handleInternalLinkClick } from "../utils/navigation";
import { normalizeSeasonNumber } from "../utils/showFields";
import { trackEvent } from "../utils/analytics";
import { normalizeSearchText } from "../services/search/searchText";
import { getWatchlistToggleLabel } from "../utils/watchlist";

function toWatchlistShow(show = {}) {
  return {
    ...show,
    id: show.id || show.slug,
    sourceName: show.sourceName || show.source,
    networks: show.networks || (show.source ? [show.source] : []),
  };
}

const ArchiveSearchResultCard = ({ show, isInWatchlist, onToggleWatchlist, position = null }) => {
  const showPath = show.slug ? `/show/${show.slug}` : null;
  const moreLikePath = show.slug && show.hasShowsLikePage ? `/shows-like/${show.slug}` : null;
  const monthPath = show.yearMonth ? `/month/${show.yearMonth}` : null;
  const watchlistShow = useMemo(() => toWatchlistShow(show), [show]);
  const inWatchlist = isInWatchlist?.(watchlistShow);
  const rating = Number.isFinite(show.rating) ? show.rating : null;
  const seasonNumber = normalizeSeasonNumber(show.seasonNumber);
  const summary = show.summary || "No summary available.";
  const matchTags = [...new Set(Array.isArray(show.matchTags) ? show.matchTags.filter(Boolean) : [])].slice(0, 2);
  const matchTagSet = new Set(matchTags.map((tag) => normalizeSearchText(tag)));
  const supportingTags = [...new Set(Array.isArray(show.tasteTags) ? show.tasteTags.filter(Boolean) : [])]
    .filter((tag) => !matchTagSet.has(normalizeSearchText(tag)))
    .slice(0, 3);

  const handleShowClick = (event) => {
    if (!showPath) return;
    trackEvent("archive_search_result_click", {
      show_slug: show.slug,
      match_mode: show.matchMode || "lexical",
      position,
    });
    handleInternalLinkClick(event, showPath);
  };

  const handleWatchlistClick = () => {
    trackEvent(inWatchlist ? "archive_search_watchlist_remove" : "archive_search_watchlist_add", {
      show_slug: show.slug,
      match_mode: show.matchMode || "lexical",
    });
    onToggleWatchlist?.(watchlistShow);
  };

  const handleMoreLikeThisClick = (event) => {
    trackEvent("archive_search_more_like_this", {
      show_slug: show.slug,
      match_mode: show.matchMode || "lexical",
      position,
    });
    if (moreLikePath) {
      handleInternalLinkClick(event, moreLikePath);
      return;
    }
  };

  return (
    <article className="group grid min-h-[188px] grid-cols-[104px_minmax(0,1fr)] overflow-hidden rounded-xl border border-accent-gold/65 bg-card text-card-foreground shadow-card transition-shadow hover:shadow-card-hover sm:min-h-[268px] sm:grid-cols-[160px_minmax(0,1fr)]">
      <a
        href={showPath || undefined}
        onClick={handleShowClick}
        className={cn(
          "relative block min-h-full overflow-hidden bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card",
          !showPath && "pointer-events-none"
        )}
        aria-label={showPath ? `View details for ${show.title}` : undefined}
        tabIndex={showPath ? undefined : -1}
      >
        {show.poster ? (
          <>
            <img
              src={show.poster}
              srcSet={show.posterMetadata?.thumbnail ? `${show.posterMetadata.thumbnail} 340w, ${show.poster} 680w` : undefined}
              sizes="(max-width: 640px) 96px, 160px"
              alt={`${show.title} poster`}
              className="h-full w-full object-cover grayscale-[12%] contrast-110 transition-transform duration-200 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/45 via-transparent to-transparent" aria-hidden="true" />
          </>
        ) : (
          <PosterPlaceholder showId={show.slug} className="h-full w-full bg-poster-placeholder-gradient" />
        )}
      </a>

      <div className="flex min-w-0 flex-col gap-2.5 p-3.5 sm:gap-4 sm:p-5">
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {show.watchlistBoosted && (
                <Chip variant="muted" size="badge" className="text-muted-foreground">
                  Similar to My List
                </Chip>
              )}
              <Typography variant="caption" className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
                <span className="line-clamp-1">{show.matchReason || "Matches your search"}</span>
              </Typography>
            </div>

            <Typography as="h3" variant="h4" className="line-clamp-2 leading-tight text-foreground font-display">
              {showPath ? (
                <a
                  href={showPath}
                  onClick={handleShowClick}
                  className="title-link"
                >
                  {show.title}
                </a>
              ) : (
                show.title
              )}
            </Typography>
          </div>

          <Button
            type="button"
            size="icon"
            variant="ghost"
            className={cn("sp-card-icon-action min-h-11 min-w-11 shrink-0", inWatchlist && "sp-card-icon-action--active")}
            onClick={handleWatchlistClick}
            aria-label={getWatchlistToggleLabel(show.title)}
            aria-pressed={Boolean(inWatchlist)}
          >
            <Heart className={cn("h-4 w-4", inWatchlist && "fill-current text-primary")} aria-hidden="true" />
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-y border-border/70 py-2 text-muted-foreground sm:py-3">
          {rating !== null ? (
            <RatingBadge variant="rating">{rating.toFixed(1)}</RatingBadge>
          ) : (
            <RatingBadge variant="unrated">N/A</RatingBadge>
          )}
          <SeasonBadge seasonNumber={seasonNumber} part={show.seasonPart} />
          {show.releaseDate && (
            <span className="inline-flex items-center gap-1 text-body-xs font-semibold">
              <CalendarDays className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
              {formatReleaseDate(show.releaseDate)}
            </span>
          )}
          {show.source && (
            <Chip variant="outline" size="badge" className="text-muted-foreground">
              {show.source}
            </Chip>
          )}
          {monthPath && (
            <a
              href={monthPath}
              onClick={(event) => handleInternalLinkClick(event, monthPath)}
              className="text-body-xs font-semibold text-primary transition-colors hover:text-foreground"
            >
              {show.monthLabel || show.yearMonth}
            </a>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {moreLikePath && (
            <Button
              asChild
              variant="filter"
              size="sm"
              className="min-h-9 gap-2 px-3 py-1.5 text-body-small"
            >
              <a
                href={moreLikePath}
                onClick={handleMoreLikeThisClick}
                aria-label={`Find more shows like ${show.title}`}
              >
                More like this
              </a>
            </Button>
          )}
        </div>

        <Typography variant="body" className="hidden text-muted-foreground sm:line-clamp-2">
          {summary}
        </Typography>

        {(matchTags.length > 0 || supportingTags.length > 0) && (
          <div className="archive-result-taste mt-auto">
            {matchTags.length > 0 && (
              <div className="archive-result-taste__group">
                <span className="archive-result-taste__label">Matches your search</span>
                <div className="archive-result-taste__tags">
                  {matchTags.map((tag) => (
                    <TasteTag key={tag} variant="matched" className="archive-result-taste__tag">
                      {tag}
                    </TasteTag>
                  ))}
                </div>
              </div>
            )}
            {supportingTags.length > 0 && (
              <div className="archive-result-taste__group archive-result-taste__group--supporting">
                <span className="archive-result-taste__label">Also in this show</span>
                <div className="archive-result-taste__tags">
                  {supportingTags.map((tag, index) => (
                    <TasteTag
                      key={tag}
                      variant="subtle"
                      className={cn("archive-result-taste__tag", index > 1 && "hidden sm:inline-flex")}
                    >
                      {tag}
                    </TasteTag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {show.trailerUrl && (
          <a
            href={show.trailerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex w-fit items-center gap-2 text-body-xs font-semibold text-muted-foreground transition-colors hover:text-primary"
            onClick={() => trackEvent("outbound_click", { surface: "archive_search_result", type: "trailer", slug: show.slug })}
          >
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Trailer
          </a>
        )}
      </div>
    </article>
  );
};

ArchiveSearchResultCard.propTypes = {
  show: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string,
    title: PropTypes.string,
    poster: PropTypes.string,
    posterMetadata: PropTypes.shape({
      thumbnail: PropTypes.string,
    }),
    releaseDate: PropTypes.string,
    yearMonth: PropTypes.string,
    monthLabel: PropTypes.string,
    source: PropTypes.string,
    sourceName: PropTypes.string,
    networks: PropTypes.arrayOf(PropTypes.string),
    rating: PropTypes.number,
    seasonNumber: PropTypes.number,
    seasonPart: PropTypes.string,
    summary: PropTypes.string,
    tasteTags: PropTypes.arrayOf(PropTypes.string),
    matchTags: PropTypes.arrayOf(PropTypes.string),
    matchReason: PropTypes.string,
    matchMode: PropTypes.string,
    watchlistBoosted: PropTypes.bool,
    hasShowsLikePage: PropTypes.bool,
    trailerUrl: PropTypes.string,
  }).isRequired,
  isInWatchlist: PropTypes.func.isRequired,
  onToggleWatchlist: PropTypes.func.isRequired,
  position: PropTypes.number,
};

export { toWatchlistShow };
export default React.memo(ArchiveSearchResultCard);
